import { backToParent } from '../../_utils/navTrail';
import React, { useState, useEffect } from 'react';
import { gate } from '../permissions/access';
import { useViewingSites } from '../multiLocation/viewingSites';
import { sitesService } from '../../_services/sites.service';
import { useLocation, useHistory } from 'react-router-dom';
import '../new-service/CatalogueTopNav.css';
import './FinanceHeader.css';
import { OverviewView, TransactionsView, ReportsView, CashUpView, CommissionsView } from './views';
import { hairdresserService } from '../../_services/hairdresser.service';
import { getUserInfo } from '../cashup/CashupUtils';
import useWindowSize from '../new-service/useWindowSize';
import NotificationBell from '../notifications/NotificationBell';
import LocationPicker from '../multiLocation/LocationPicker';

// Color palette for staff avatars
const staffColors = [
  '#8b7355', '#c4a882', '#6b7280', '#a78bfa', '#60a5fa',
  '#34d399', '#f472b6', '#fbbf24', '#ef4444', '#14b8a6'
];

// Helper to get initials from name
const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase();
};

// Helper to get a consistent color for a staff member based on their ID
const getStaffColor = (id, index) => {
  if (!id) return staffColors[index % staffColors.length];
  // Use a simple hash of the ID to get a consistent color
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash) + id.charCodeAt(i);
    hash = hash & hash;
  }
  return staffColors[Math.abs(hash) % staffColors.length];
};

// Multi-location: the "All locations" tab only exists for accounts with 2+ locations.
function isMultiLocation() {
  try { const c = JSON.parse(sessionStorage.getItem('ml:sites') || 'null'); return !!(c && c.data && c.data.sites && c.data.sites.length > 1); } catch (e) { return false; }
}

const tabs = [
  { id: 'overview', label: 'Overview' },
  { id: 'transactions', label: 'Transactions' },
  { id: 'commissions', label: 'Commissions' },
  { id: 'reports', label: 'Reports' },
  { id: 'cashup', label: 'Cash Up' },
  // The 'All locations' tab retired when the header Locations filter landed:
  // every view reports on the chosen sites, with a By location block in
  // Overview, so a separate tab was one more place to look for the same thing.
];

const backArrowSrc = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%231a1a1a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cline x1='19' y1='12' x2='5' y2='12'/%3E%3Cpolyline points='12 19 5 12 12 5'/%3E%3C/svg%3E";
const BackArrowIcon = () => (
  <img src={backArrowSrc} alt="Back" width="20" height="20" style={{ display: 'block' }} />
);

const Finance = (props) => {
  const location = useLocation();
  const history = useHistory();
  const { width } = useWindowSize();
  const isMobile = width <= 1100;

  // Read initial tab from URL query param
  const queryParams = new URLSearchParams(location.search);
  // /transactions/:transactionId deep-links straight to one transaction's
  // detail panel (notifications use this).
  const routeTransactionId = props.match?.params?.transactionId || null;
  const initialTab = routeTransactionId ? 'transactions' : (queryParams.get('tab') || 'overview');
  const initialType = queryParams.get('type'); // deep-link e.g. ?tab=transactions&type=product
  const initialReport = queryParams.get('report'); // deep-link e.g. ?tab=reports&report=rebook-rate

  const [activeTab, setActiveTab] = useState(initialTab);
  const [staffFilter, setStaffFilter] = useState('all');
  // Which locations Finance is showing: the app-wide selection made in the
  // header Location picker (multiLocation/viewingSites.js). Defaults to the
  // location this device works at; cleared when the device switches.
  // `salonIdsParam` is null unless it spans more than this location, so the
  // request is byte-for-byte what it has always been for one site.
  const { param: salonIdsParam, selectedSites } = useViewingSites();
  const [staffDropdownOpen, setStaffDropdownOpen] = useState(false);
  const [staffSearchQuery, setStaffSearchQuery] = useState('');
  const staffSearchRef = React.useRef(null);
  const [dateFilter, setDateFilter] = useState('mtd');
  const [compareFilter, setCompareFilter] = useState('previous');
  const [granularity, setGranularity] = useState('daily');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [transactionFilter, setTransactionFilter] = useState(initialType || 'all');

  // Sync tab when URL query param changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam && tabs.find(t => t.id === tabParam)) {
      setActiveTab(tabParam);
    }
    const typeParam = params.get('type');
    if (typeParam) setTransactionFilter(typeParam);
  }, [location.search]);

  // Check if user is salon owner
  const { isSalonOwner, employmentTypeId } = getUserInfo();
  const isSelfEmployed = employmentTypeId === 2 && isSalonOwner !== 1;
  // Roles & permissions: other people's takings need `reports` (the server
  // returns own figures only without it). Flag off: as before.
  const canViewAllStaff = !isSelfEmployed && gate('reports', true);

  // Staff list state - fetched from API
  const [staffList, setStaffList] = useState([
    { id: 'all', name: 'All Staff', initials: null, color: null }
  ]);
  const [staffLoading, setStaffLoading] = useState(true);
  
  // Fetch staff list from API (only for salon owners)
  useEffect(() => {
    // Only fetch staff list if user is a salon owner
    if (!canViewAllStaff) {
      setStaffLoading(false);
      return;
    }

    const fetchStaff = async () => {
      try {
        setStaffLoading(true);
        // Several locations selected: everyone who works at any of them, so
        // the staff filter matches the figures (it used to list only this
        // location's team while the numbers spanned several).
        if (salonIdsParam) {
          const r = await sitesService.groupStaff(salonIdsParam);
          const rows = (r && r.staff) || [];
          setStaffList([
            { id: 'all', name: 'All Staff', initials: null, color: null },
            ...rows.map((st, index) => ({
              id: String(st.userId),
              name: st.name || 'Unknown',
              initials: getInitials(st.name || ''),
              color: getStaffColor(String(st.userId), index),
              employmentTypeId: 1
            }))
          ]);
          return;
        }
        const hairdresserId = localStorage.getItem('userId');
        
        // Use staffListServiceV2WithoutService which is what the Staff component uses
        const response = await hairdresserService.staffListServiceV2WithoutService({
          hairdresserId,
          page: 1,
          listType: 1
        });
        
        // Handle various response structures
        // API returns: response.data.data.total (array of staff)
        let staffData = null;
        if (response?.data?.data?.total && Array.isArray(response.data.data.total)) {
          staffData = response.data.data.total;
        } else if (response?.data?.data?.hairdresser && Array.isArray(response.data.data.hairdresser)) {
          staffData = response.data.data.hairdresser;
        } else if (response?.data?.data && Array.isArray(response.data.data)) {
          staffData = response.data.data;
        } else if (response?.data?.responseData?.total && Array.isArray(response.data.responseData.total)) {
          staffData = response.data.responseData.total;
        } else if (response?.data?.responseData && Array.isArray(response.data.responseData)) {
          staffData = response.data.responseData;
        } else if (response?.data?.hairdresser && Array.isArray(response.data.hairdresser)) {
          staffData = response.data.hairdresser;
        } else if (response?.data && Array.isArray(response.data)) {
          staffData = response.data;
        }
        
        console.log('Staff API response:', response?.data);
        console.log('Extracted staff data:', staffData);
        
        if (staffData && staffData.length > 0) {
          // Transform API data to match our format
          const formattedStaff = staffData.map((staff, index) => {
            // The name is directly on the staff object, not in hairdresserInfo
            // hairdresserInfo contains additional details like jobTitle, employmentTypeId, etc.
            const staffName = staff.name ||
                            (staff.hairdresserInfo?.name) ||
                            `${staff.firstName || ''} ${staff.lastName || ''}`.trim() ||
                            'Unknown';
            const staffId = staff._id || staff.hairdresserInfo?._id || staff.userId || staff.id || staff.hairdresserId;
            
            console.log('Processing staff:', staff.name, staffId);
            
            return {
              id: staffId,
              name: staffName,
              initials: getInitials(staffName),
              color: getStaffColor(staffId, index),
              employmentTypeId: staff.hairdresserInfo?.employmentTypeId || 1
            };
          });
          
          // Add "All Staff" option at the beginning
          setStaffList([
            { id: 'all', name: 'All Staff', initials: null, color: null },
            ...formattedStaff
          ]);
        }
      } catch (error) {
        console.error('Failed to fetch staff list:', error);
        // Keep the default "All Staff" option on error
      } finally {
        setStaffLoading(false);
      }
    };

    fetchStaff();
  }, [canViewAllStaff, salonIdsParam]);
  
  const handleViewCommissionPayments = () => {
    setActiveTab('commissions');
  };
  
  const filteredStaffList = staffSearchQuery
    ? staffList.filter(s => s.id === 'all' || s.name.toLowerCase().includes(staffSearchQuery.toLowerCase()))
    : staffList;

  const openStaffDropdown = () => {
    setStaffDropdownOpen(true);
    setTimeout(() => staffSearchRef.current?.focus(), 50);
  };

  const closeStaffDropdown = () => {
    setStaffDropdownOpen(false);
    setStaffSearchQuery('');
  };

  const selectedStaff = staffList.find(s => s.id === staffFilter) || staffList[0];

  // Get current tab label for mobile header
  const currentTabLabel = tabs.find(t => t.id === activeTab)?.label || 'Finance';

  return (
    <div className="h_n catalogue-no-header">
      <div className="body_container cm_bg_white">
        <div className="catalogue-page-wrapper">
          {/* Mobile: Back arrow header with staff dropdown */}
          {isMobile && (
            <div className="catalogue-mobile-header">
              <button
                className="catalogue-mobile-back-btn"
                onClick={() => backToParent(history, '/finance-nav')}
              >
                <BackArrowIcon />
              </button>
              <h1 className="catalogue-mobile-title">
                {currentTabLabel}
                {tabs.find(t => t.id === activeTab)?.beta && (
                  <span style={{
                    fontSize: '9px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '1px 5px',
                    borderRadius: '4px',
                    backgroundColor: '#f0f0f0',
                    color: '#737373',
                    marginLeft: '6px',
                    verticalAlign: 'middle',
                    lineHeight: '16px'
                  }}>Beta</span>
                )}
              </h1>
              <div className="catalogue-mobile-actions fin-mobile-actions">
                <LocationPicker slot="mobile" />
                {canViewAllStaff && <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => staffDropdownOpen ? closeStaffDropdown() : openStaffDropdown()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 10px',
                      border: '1px solid #e5e5e5',
                      borderRadius: '9999px',
                      background: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    {staffFilter === 'all' ? (
                      <div style={{ display: 'flex' }}>
                        <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#d1d5db', border: '2px solid white', marginRight: '-5px', zIndex: 3 }} />
                        <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#c4a882', border: '2px solid white', marginRight: '-5px', zIndex: 2 }} />
                        <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#8b7355', border: '2px solid white', zIndex: 1 }} />
                      </div>
                    ) : (
                      <div style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: selectedStaff.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: '600',
                        color: 'white'
                      }}>
                        {selectedStaff.initials}
                      </div>
                    )}
                    <svg
                      style={{ width: '12px', height: '12px', color: '#737373', transform: staffDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {staffDropdownOpen && (
                    <>
                      <div
                        style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                        onClick={closeStaffDropdown}
                      />
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: '8px',
                        width: '220px',
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                        border: '1px solid #e5e5e5',
                        zIndex: 50,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: '60vh'
                      }}>
                        {staffList.length > 6 && (
                          <div style={{ padding: '8px 8px 0' }}>
                            <input
                              ref={staffSearchRef}
                              type="text"
                              placeholder="Search staff..."
                              value={staffSearchQuery}
                              onChange={(e) => setStaffSearchQuery(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px 10px',
                                border: '1px solid #e5e5e5',
                                borderRadius: '8px',
                                fontSize: '13px',
                                outline: 'none',
                                boxSizing: 'border-box'
                              }}
                              onFocus={(e) => e.target.style.borderColor = '#007EA0'}
                              onBlur={(e) => e.target.style.borderColor = '#e5e5e5'}
                            />
                          </div>
                        )}
                        <div style={{ padding: '8px', overflowY: 'auto' }}>
                          {filteredStaffList.map((staff) => (
                            <button
                              key={staff.id}
                              onClick={() => {
                                setStaffFilter(staff.id);
                                closeStaffDropdown();
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                width: '100%',
                                padding: '10px 12px',
                                border: 'none',
                                borderRadius: '8px',
                                background: staffFilter === staff.id ? '#f5f5f5' : 'transparent',
                                cursor: 'pointer',
                                textAlign: 'left'
                              }}
                            >
                              {staff.id === 'all' ? (
                                <div style={{ display: 'flex', marginLeft: '-4px' }}>
                                  <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#d1d5db', border: '2px solid white', marginRight: '-6px', zIndex: 3 }} />
                                  <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#c4a882', border: '2px solid white', marginRight: '-6px', zIndex: 2 }} />
                                  <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#8b7355', border: '2px solid white', zIndex: 1 }} />
                                </div>
                              ) : (
                                <div style={{
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  backgroundColor: staff.color,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '10px',
                                  fontWeight: '600',
                                  color: 'white'
                                }}>
                                  {staff.initials}
                                </div>
                              )}
                              <span style={{ fontSize: '14px', color: '#1a1a1a', fontWeight: staffFilter === staff.id ? '500' : '400' }}>
                                {staff.name}
                              </span>
                              {staffFilter === staff.id && (
                                <svg style={{ width: '16px', height: '16px', color: '#007EA0', marginLeft: 'auto' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </button>
                          ))}
                          {filteredStaffList.length === 0 && (
                            <div style={{ padding: '12px', textAlign: 'center', fontSize: '13px', color: '#999' }}>
                              No staff found
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>}
              </div>
            </div>
          )}

          {/* Desktop: Finance Top Nav - Sticky tabs like CatalogueTopNav */}
          {!isMobile && (
          <div className="catalogue-top-nav">
            <div className="catalogue-top-nav-container">
              <div className="catalogue-top-nav-content">
                {/* Left: Navigation Tabs */}
                <nav className="catalogue-tabs-nav">
                  {tabs.filter(t => !t.multiOnly || isMultiLocation()).map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => {
                        if (tab.href) {
                          history.push(tab.href);
                          return;
                        }
                        setActiveTab(tab.id);
                        if (tab.id === 'transactions') {
                          setTransactionFilter('all');
                        }
                      }}
                      className={`catalogue-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                    >
                      <span>{tab.label}</span>
                      {tab.beta && (
                        <span style={{
                          fontSize: '9px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          padding: '1px 5px',
                          borderRadius: '4px',
                          backgroundColor: activeTab === tab.id ? '#007EA0' : '#f0f0f0',
                          color: activeTab === tab.id ? 'white' : '#737373',
                          marginLeft: '6px',
                          lineHeight: '16px'
                        }}>Beta</span>
                      )}
                    </button>
                  ))}
                </nav>

                {/* Right: Staff Filter Dropdown */}
                <div className="catalogue-nav-actions">
                  {canViewAllStaff && (
                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={() => staffDropdownOpen ? closeStaffDropdown() : openStaffDropdown()}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 1rem',
                          border: '1px solid #e5e5e5',
                          borderRadius: '9999px',
                          background: 'white',
                          cursor: 'pointer'
                        }}
                      >
                      {staffFilter === 'all' ? (
                        <div style={{ display: 'flex' }}>
                          <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#d1d5db', border: '2px solid white', marginRight: '-6px', zIndex: 3 }}></div>
                          <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#c4a882', border: '2px solid white', marginRight: '-6px', zIndex: 2 }}></div>
                          <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#8b7355', border: '2px solid white', zIndex: 1 }}></div>
                        </div>
                      ) : (
                        <div style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          backgroundColor: selectedStaff.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '11px',
                          fontWeight: '600',
                          color: 'white'
                        }}>
                          {selectedStaff.initials}
                        </div>
                      )}
                      <span style={{ fontSize: '14px', color: '#1a1a1a' }}>{selectedStaff.name}</span>
                      <svg
                        style={{
                          width: '14px',
                          height: '14px',
                          color: '#737373',
                          transform: staffDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s'
                        }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Dropdown Menu */}
                    {staffDropdownOpen && (
                      <>
                        <div
                          style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                          onClick={closeStaffDropdown}
                        />
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          marginTop: '8px',
                          width: '240px',
                          backgroundColor: 'white',
                          borderRadius: '12px',
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          border: '1px solid #e5e5e5',
                          zIndex: 50,
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          maxHeight: '400px'
                        }}>
                          {staffList.length > 6 && (
                            <div style={{ padding: '8px 8px 0' }}>
                              <input
                                ref={staffSearchRef}
                                type="text"
                                placeholder="Search staff..."
                                value={staffSearchQuery}
                                onChange={(e) => setStaffSearchQuery(e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px 10px',
                                  border: '1px solid #e5e5e5',
                                  borderRadius: '8px',
                                  fontSize: '13px',
                                  outline: 'none',
                                  boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#007EA0'}
                                onBlur={(e) => e.target.style.borderColor = '#e5e5e5'}
                              />
                            </div>
                          )}
                          <div style={{ padding: '8px', overflowY: 'auto' }}>
                            {filteredStaffList.map((staff) => (
                              <button
                                key={staff.id}
                                onClick={() => {
                                  setStaffFilter(staff.id);
                                  closeStaffDropdown();
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  width: '100%',
                                  padding: '10px 12px',
                                  border: 'none',
                                  borderRadius: '8px',
                                  background: staffFilter === staff.id ? '#f5f5f5' : 'transparent',
                                  cursor: 'pointer',
                                  textAlign: 'left',
                                  transition: 'background 0.15s'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.background = '#f5f5f5'}
                                onMouseOut={(e) => e.currentTarget.style.background = staffFilter === staff.id ? '#f5f5f5' : 'transparent'}
                              >
                                {staff.id === 'all' ? (
                                  <div style={{ display: 'flex', marginLeft: '-4px' }}>
                                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#d1d5db', border: '2px solid white', marginRight: '-6px', zIndex: 3 }}></div>
                                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#c4a882', border: '2px solid white', marginRight: '-6px', zIndex: 2 }}></div>
                                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#8b7355', border: '2px solid white', zIndex: 1 }}></div>
                                  </div>
                                ) : (
                                  <div style={{
                                    width: '28px',
                                    height: '28px',
                                    borderRadius: '50%',
                                    backgroundColor: staff.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '10px',
                                    fontWeight: '600',
                                    color: 'white'
                                  }}>
                                    {staff.initials}
                                  </div>
                                )}
                                <span style={{
                                  fontSize: '14px',
                                  color: '#1a1a1a',
                                  fontWeight: staffFilter === staff.id ? '500' : '400'
                                }}>
                                  {staff.name}
                                </span>
                                {staffFilter === staff.id && (
                                  <svg
                                    style={{ width: '16px', height: '16px', color: '#007EA0', marginLeft: 'auto' }}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </button>
                            ))}
                            {filteredStaffList.length === 0 && (
                              <div style={{ padding: '12px', textAlign: 'center', fontSize: '13px', color: '#999' }}>
                                No staff found
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="notif-bell-pinned-wrap">
              <LocationPicker />
              <NotificationBell />
            </div>
          </div>
          )}

          {/* Main Content */}
          <div className="catalogue-content-container">
            {activeTab === 'overview' && (
              <OverviewView
                granularity={granularity}
                setGranularity={setGranularity}
                dateFilter={dateFilter}
                setDateFilter={setDateFilter}
                compareFilter={compareFilter}
                setCompareFilter={setCompareFilter}
                onViewCommission={handleViewCommissionPayments}
                staffFilter={staffFilter}
                staffList={staffList}
                salonIds={salonIdsParam}
                selectedSites={selectedSites}
              />
            )}
            {activeTab === 'transactions' && (
              <TransactionsView
                selectedTransaction={selectedTransaction}
                setSelectedTransaction={setSelectedTransaction}
                initialFilter={transactionFilter}
                staffFilter={staffFilter}
                initialTransactionId={routeTransactionId}
                salonIds={salonIdsParam}
                selectedSites={selectedSites}
              />
            )}
            {activeTab === 'commissions' && <CommissionsView staffFilter={staffFilter} staffList={staffList} salonIds={salonIdsParam} selectedSites={selectedSites} />}
            {activeTab === 'reports' && <ReportsView staffFilter={staffFilter} staffList={staffList} initialReport={initialReport} salonIds={salonIdsParam} selectedSites={selectedSites} />}
            {activeTab === 'cashup' && <CashUpView staffFilter={staffFilter} salonIds={salonIdsParam} selectedSites={selectedSites} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Finance;
